/**
 * McaPagination — the standard windowed page-number footer used across every
 * list/results table (collections, ASIN/SKU records, category products, …).
 *
 * Standard: 50 rows per page (PAGE_SIZE), windowed numbers with ellipses, prev/
 * next chevrons. `page` is 1-indexed. Renders nothing when there's a single page.
 *
 * Usage: <McaPagination page={page} totalPages={n} onChange={setPage} />
 */

export const PAGE_SIZE = 50;

// Windowed page-number list (with ellipses).
const getPageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "ellipsis", total];
  if (current >= total - 3)
    return [1, "ellipsis", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "ellipsis", current - 1, current, current + 1, "ellipsis", total];
};

export const McaPagination = ({ page, totalPages, onChange }) => {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <div className="mca-pagination">
      <button
        className="mca-page-btn"
        disabled={page <= 1}
        onClick={() => onChange(Math.max(1, page - 1))}
        aria-label="Previous"
        type="button"
      >
        <svg
          viewBox="0 0 24 24"
          width="15"
          height="15"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m15 6-6 6 6 6" />
        </svg>
      </button>
      {getPageList(page, totalPages).map((p, i) =>
        p === "ellipsis" ? (
          <span key={`e${i}`} className="mca-page-ellipsis" aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={p}
            className="mca-page-btn"
            aria-current={p === page ? "page" : undefined}
            onClick={() => onChange(p)}
            type="button"
          >
            {p}
          </button>
        )
      )}
      <button
        className="mca-page-btn"
        disabled={page >= totalPages}
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        aria-label="Next"
        type="button"
      >
        <svg
          viewBox="0 0 24 24"
          width="15"
          height="15"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m9 6 6 6-6 6" />
        </svg>
      </button>
    </div>
  );
};
